/**
 * Contact — mock "create support request" endpoint.
 *
 * The real backend route does NOT exist yet. This module stands in for it: it
 * defines the EXACT payload the API will receive and resolves after a short
 * simulated latency, so wiring the real endpoint later is a one-line swap. When
 * the API lands, replace the body of {@link submitContactRequest} with the axios
 * POST below; the payload shape and every call site stay unchanged.
 *
 * For the future implementer, the intended call (mirrors the other owner APIs):
 *
 *   const res = await axios.post(
 *     `${window.config.userApiUrl}/support/contact`,
 *     payload,                                   // ContactRequestPayload, verbatim
 *     { headers: { Authorization: accessToken } } // from state.authentication.access_token
 *   );
 *   return { ok: true, ticketId: res.data.ticket_id };
 */
import { MOCK_LATENCY } from '../../theme/tokens';

/** The support category the owner picks from the dropdown. */
export type ContactTopic = 'general' | 'billing' | 'technical' | 'account' | 'feature' | 'other';

/**
 * Dropdown options. `label` is plain English copy, which doubles as the i18n key
 * (the shared `Select` runs each option label through `t()` at render).
 */
export const CONTACT_TOPICS: ReadonlyArray<{ value: ContactTopic; label: string }> = [
  { value: 'general', label: 'General question' },
  { value: 'billing', label: 'Billing & payments' },
  { value: 'technical', label: 'Technical issue' },
  { value: 'account', label: 'Account & access' },
  { value: 'feature', label: 'Feature request' },
  { value: 'other', label: 'Something else' },
];

/** The fields the owner fills in. */
export type ContactFormValues = {
  name: string;
  email: string;
  topic: ContactTopic;
  subject: string;
  message: string;
};

/**
 * Ambient application state attached to every request alongside the form fields,
 * so support has the full context without the owner re-typing it. Each field is
 * nullable: it is `null` when the app can't resolve it (e.g. no brand selected
 * yet, or a config key absent in this build).
 */
export type ContactContext = {
  /** Authenticated owner (auth slice). */
  userId: string | null;
  accountEmail: string | null;
  /** Currently selected brand (Owner Studio brand switcher / casinoInstance slice). */
  brandId: string | null;
  brandName: string | null;
  brandTenantId: string | null;
  /** Host / tenant identifiers from the injected runtime config (`window.config`). */
  siteId: string | null;
  tenantId: string | null;
  /** Environment context, useful for triage. */
  locale: string;
  pageUrl: string;
  userAgent: string;
  /** ISO-8601 capture time. */
  submittedAt: string;
};

/** The full POST body: what the owner typed, plus the app-state context. */
export type ContactRequestPayload = ContactFormValues & { context: ContactContext };

export type ContactSubmitResult = { ok: true; ticketId: string };

/** Simulated network latency, drawn from the area's mock-latency band. */
const mockDelay = (): Promise<void> => {
  const { MIN_MS, MAX_MS } = MOCK_LATENCY;
  const ms = MIN_MS + Math.random() * (MAX_MS - MIN_MS);
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * MOCK submit. Logs the assembled payload (so the shape is visible while the real
 * API is pending) and resolves with a fake ticket id after a short delay. Swap the
 * body for the axios POST documented at the top of this file when the endpoint is
 * ready, keeping the same signature.
 */
export const submitContactRequest = async (payload: ContactRequestPayload): Promise<ContactSubmitResult> => {
  // Visible while mocked: confirms the body that the real endpoint will receive.
  // eslint-disable-next-line no-console
  console.info('[contact] mock submit payload →', payload);
  await mockDelay();
  return { ok: true, ticketId: `CS-${Date.now().toString(36).toUpperCase()}` };
};